import React from 'react';
import { CmsBrowseResultBanner } from 'js/model/cms/cms-browse-result-banner';
import clsx from 'clsx';
import { Button, ButtonColour, ButtonSize } from 'js/components/Button';
import { trackStructuredEvent } from 'js/helpers/google-tag-manager';
import { parseCmsBoolean } from 'js/model/cms/boolean';
import { InView } from 'react-intersection-observer';
import styles from './BrowseResultBanner.module.css';

interface Props {
  matchingBanner: Partial<CmsBrowseResultBanner>;
  isInHybridView?: boolean;
}

export function BrowseResultBanner({
  matchingBanner,
  isInHybridView,
}: Props): React.ReactElement | null {
  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const imageRef = React.useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    updateContainerWidth();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const updateContainerWidth = () => {
    setContainerWidth(window.innerWidth);
  };

  const onResize = () => {
    updateContainerWidth();
  };

  function isChildOf(element: HTMLElement, parent: HTMLElement): boolean {
    if (element.parentElement === null) {
      return false;
    }

    if (element.parentElement === parent) {
      return true;
    }

    return isChildOf(element.parentElement, parent);
  }

  const onClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();

    if (event.target === imageRef.current) {
      trackClick('image');
    } else if (
      event.target === buttonRef.current ||
      isChildOf(event.target as HTMLElement, buttonRef.current as HTMLElement)
    ) {
      trackClick('button');
    } else {
      trackClick();
    }

    if (link && ctaText) {
      window.location.href = link;
    }
  };

  const isMobile = containerWidth < 520;
  const isSmallerThanDesktop = containerWidth < 993;

  const {
    'image-mobile': imageMobile,
    'image-desktop': imageDesktop,
    title,
    text,
    'background-colour': backgroundColor,
    colour,
    link,
    'campaign-id': campaignId,
    'cta-text': ctaText,
    enabled,
  } = matchingBanner;

  const category = isInHybridView ? 'browse_hybrid' : 'browse_list';
  const textIndent = isInHybridView || isSmallerThanDesktop ? 0 : 24;

  const trackClick = async (label?: string) => {
    await trackStructuredEvent({
      category,
      value: campaignId,
      property: 'browse_card_banner',
      action: 'click',
      label,
    });
  };

  function trackBannerHasBeenDisplayed(inView: boolean): void {
    if (inView) {
      trackStructuredEvent({
        category,
        value: campaignId,
        property: 'browse_card_banner',
        action: 'view',
      });
    }
  }

  if (!parseCmsBoolean(enabled)) {
    return null;
  }

  return (
    <InView triggerOnce onChange={trackBannerHasBeenDisplayed}>
      <a
        className={clsx(styles.container, {
          [styles.hasLink]: link && ctaText,
        })}
        onClick={(event) => {
          onClick(event);
        }}
        style={{
          backgroundColor,
          color: colour,
          cursor: link && ctaText ? 'pointer' : 'default',
        }}
      >
        <div className={styles.imageContainer} data-testid="banner-container">
          <img
            ref={imageRef}
            className={styles.image}
            src={isMobile ? imageMobile : imageDesktop}
            alt=""
          />
        </div>
        <div className={styles.textArea}>
          <div className={styles.title}>{title}</div>
          <div
            className={styles.text}
            style={{ paddingLeft: `${textIndent}px` }}
          >
            {text}
          </div>
          {ctaText && link && (
            <div
              ref={buttonRef}
              className={styles.buttonContainer}
              style={{ paddingLeft: `${textIndent}px` }}
              data-testid="button-container"
            >
              <Button
                label={ctaText}
                colour={ButtonColour.Blue}
                fullWidth={false}
                size={ButtonSize.Medium}
              />
            </div>
          )}
        </div>
      </a>
    </InView>
  );
}
